// SR-WIRE — Slip Repair API helpers.
//
// Pure async functions wrapping the live edge-fn surface. The UI hooks
// and components call these instead of stub handlers. All requests go
// through the shared browser supabase client so auth headers flow
// automatically.
//
// Routes:
//   POST   slip-repair-api/upload           — uploadSlip
//   POST   slip-repair-api/from-extractor   — (not wired in this slice)
//   GET    slip-repair-api/session/:id      — read via useSlipRepair hook
//   PATCH  slip-repair-api/leg/:id          — patchLeg
//   GET    slip-repair-api/history          — fetchHistory
//   POST   slip-repair-run                  — runRepair
//
// All responses are envelope `{ ok: true, ... }` from the backend. We
// throw on `ok === false` so callers can try/catch.

import { getBrowserClient } from "../../supabase/browser.ts";
import type { SessionRow } from "./useSlipRepair.ts";

interface ApiError extends Error {
  code?: string;
  status?: number;
}

function asError(message: string, code?: string, status?: number): ApiError {
  const e = new Error(message) as ApiError;
  if (code) e.code = code;
  if (typeof status === "number") e.status = status;
  return e;
}

interface Envelope {
  ok?: boolean;
  error?: { code?: string; message?: string };
  [k: string]: unknown;
}

async function invokeOrThrow(
  fnPath: string,
  init: { method: "GET" | "POST" | "PATCH"; body?: Record<string, unknown> },
): Promise<Record<string, unknown>> {
  const client = getBrowserClient();
  const opts: { method: "GET" | "POST" | "PATCH"; body?: Record<string, unknown> } = { method: init.method };
  if (init.body !== undefined) opts.body = init.body;
  const { data, error } = await client.functions.invoke(fnPath, opts);
  if (error) {
    throw asError(error.message ?? "Edge function call failed", "network_error");
  }
  const env = (data ?? {}) as Envelope;
  if (env.ok === false) {
    const msg = env.error?.message ?? "Edge function returned ok=false";
    throw asError(msg, env.error?.code);
  }
  return env as Record<string, unknown>;
}

// ─── Upload ────────────────────────────────────────────────────

export interface UploadSlipInput {
  // Preferred: storage-first upload (avoids the 6MB JSON-body limit).
  slip_image_path?: string;
  slip_image_mime?: string;
  slip_image_size_bytes?: number;
  // Legacy fallback for tiny payloads only (kept for completeness; never used by UI).
  image_base64?: string;
  pdf_base64?: string;
  bookmaker_hint?: string;
  entry_mode?: "pre_placement" | "post_build";
  // SR-USER-PROMPT — free-text + tag-shaped house rules. Both are
  // persisted on the session and read by Expert Voice at run time.
  user_instructions?: string;
  user_hints?: string[];
}

export interface UploadSlipResult {
  session_id: string;
  leg_count: number;
  bookmaker?: string | null;
}

export async function uploadSlip(input: UploadSlipInput): Promise<UploadSlipResult> {
  const env = await invokeOrThrow("slip-repair-api/upload", {
    method: "POST",
    body: input as unknown as Record<string, unknown>,
  });
  const sessionId = String(env.session_id ?? "");
  if (!sessionId) throw asError("Upload returned no session_id", "bad_response");
  return {
    session_id: sessionId,
    leg_count: typeof env.leg_count === "number" ? env.leg_count : 0,
    bookmaker: (env.bookmaker as string | null | undefined) ?? null,
  };
}

// ─── Run repair ────────────────────────────────────────────────

export interface RunRepairInput {
  session_id: string;
  // SR-TIERS — the user-facing tier the UI was on when Run was clicked.
  // The backend computes ALL FOUR tiers regardless (one pass over the
  // substrate); the `tier` field is informational + used to seed the
  // initial render. Legacy "sharp"/"safer"/"both" still accepted at the
  // edge fn for backwards-compat callers.
  tier?: "bold" | "moderate" | "safer" | "conservative";
  mode?: "sharp" | "safer" | "both";
  force?: boolean;
}

export async function runRepair(input: RunRepairInput): Promise<void> {
  await invokeOrThrow("slip-repair-run", {
    method: "POST",
    body: input as unknown as Record<string, unknown>,
  });
}

// ─── Patch leg ─────────────────────────────────────────────────

export interface PatchLegInput {
  leg_id: string;
  user_action: "ACCEPTED" | "OVERRIDDEN" | "REMOVED";
  user_mode_chosen?: "sharp" | "safer";
}

export interface PatchLegResult {
  session_totals: {
    sharp: number;
    safer: number;
    void_high_after: number;
  };
}

export async function patchLeg(input: PatchLegInput): Promise<PatchLegResult> {
  const body: Record<string, unknown> = { user_action: input.user_action };
  if (input.user_mode_chosen) body.user_mode_chosen = input.user_mode_chosen;
  const env = await invokeOrThrow(`slip-repair-api/leg/${input.leg_id}`, {
    method: "PATCH",
    body,
  });
  const totals = (env.totals ?? {}) as {
    total_odds_sharp?: number;
    total_odds_safer?: number;
    void_high_after?: number;
  };
  return {
    session_totals: {
      sharp: typeof totals.total_odds_sharp === "number" ? totals.total_odds_sharp : 0,
      safer: typeof totals.total_odds_safer === "number" ? totals.total_odds_safer : 0,
      void_high_after: typeof totals.void_high_after === "number" ? totals.void_high_after : 0,
    },
  };
}

// ─── History ───────────────────────────────────────────────────

export async function fetchHistory(): Promise<SessionRow[]> {
  const env = await invokeOrThrow("slip-repair-api/history", { method: "GET" });
  const list = Array.isArray(env.sessions) ? env.sessions : [];
  return list as SessionRow[];
}

// ─── Export PDF ────────────────────────────────────────────────
//
// PDF rendering is browser-side (jsPDF can't run in Deno edge). The
// endpoint returns a doctrine-clean projection of the session + legs
// + excluded markets + referral code. We hand that to the renderer
// in src/repair/repairSharePdf.ts and trigger a browser download.

export interface ExportPdfPayload {
  session: Record<string, unknown>;
  legs: Record<string, unknown>[];
  excluded_markets: { label: string; reason: string }[];
  referral_code: string | null;
  mode: "sharp" | "safer" | "both";
}

export async function exportPdf(
  sessionId: string,
  mode?: "sharp" | "safer" | "both",
): Promise<ExportPdfPayload> {
  const body: Record<string, unknown> = { session_id: sessionId };
  if (mode) body.mode = mode;
  const env = await invokeOrThrow("slip-repair-api/export-pdf", {
    method: "POST",
    body,
  });
  return {
    session: (env.session as Record<string, unknown>) ?? {},
    legs: Array.isArray(env.legs) ? (env.legs as Record<string, unknown>[]) : [],
    excluded_markets: Array.isArray(env.excluded_markets)
      ? (env.excluded_markets as { label: string; reason: string }[])
      : [],
    referral_code: (env.referral_code as string | null) ?? null,
    mode: (env.mode as ExportPdfPayload["mode"]) ?? "both",
  };
}

// ─── Share link (72h) ──────────────────────────────────────────

export interface ShareResult {
  share_url: string;
  token: string;
  expires_at: string;
  reused: boolean;
}

export async function createShareLink(sessionId: string): Promise<ShareResult> {
  const env = await invokeOrThrow("slip-repair-api/share", {
    method: "POST",
    body: { session_id: sessionId },
  });
  return {
    share_url: String(env.share_url ?? ""),
    token: String(env.token ?? ""),
    expires_at: String(env.expires_at ?? ""),
    reused: env.reused === true,
  };
}

// ─── File → base64 helper (used by UploadScreen) ───────────────

export function fileToBase64(file: File): Promise<{ base64: string; isPdf: boolean }> {
  return new Promise((resolve, reject) => {
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    const reader = new FileReader();
    reader.onerror = () => reject(asError("Failed to read file", "file_read_error"));
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      // Strip the `data:<mime>;base64,` prefix.
      const idx = result.indexOf(",");
      const base64 = idx >= 0 ? result.slice(idx + 1) : result;
      resolve({ base64, isPdf });
    };
    reader.readAsDataURL(file);
  });
}
